/**
 * A member's view of one community event: what it is, who is hosting, and the way in —
 * nothing, an RSVP, or a ticket.
 *
 * Tickets use the same Stripe Elements flow as a session booking. The seat is held while the
 * card is typed and released if the payment is abandoned, so a sold-out event is genuinely sold.
 */
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import {
  Calendar, Clock, MapPin, Link2, Users, Ticket, ArrowLeft, Check, AlertCircle,
} from "lucide-react";
import { stripePromise } from "@/lib/stripe";
import { apiFetch } from "./marketplace/api";

function TicketForm({ eventId, amountLabel, onDone }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const pay = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    setError("");
    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
    });
    if (stripeError) {
      setError(stripeError.message || "The card was not accepted.");
      setBusy(false);
      return;
    }
    try {
      await apiFetch(`/events/${eventId}/ticket/confirm`, { method: "POST" });
      onDone();
    } catch (e) {
      setError(e?.message || "Paid, but we could not confirm it. Please contact support.");
    }
    setBusy(false);
  };

  return (
    <div data-testid="event-ticket-form">
      <PaymentElement />
      {error && (
        <p className="text-sm text-rose-700 mt-3" data-testid="event-ticket-error">{error}</p>
      )}
      <button
        onClick={pay}
        disabled={busy || !stripe}
        data-testid="event-ticket-pay"
        className="mt-4 w-full rounded-lg bg-[#1a5a44] px-4 py-2.5 text-sm font-semibold text-white
                   transition hover:bg-[#24755a] disabled:opacity-50"
      >
        {busy ? "Taking payment…" : `Pay ${amountLabel}`}
      </button>
    </div>
  );
}

export default function EventDetailPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setData(await apiFetch(`/events/${eventId}`));
    } catch (e) {
      setError(e?.message || "This event could not be found.");
    }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  const rsvp = async () => {
    setBusy(true); setError("");
    try {
      const r = await apiFetch(`/events/${eventId}/rsvp`, { method: "POST" });
      setMessage(r.message);
      await load();
    } catch (e) {
      setError(e?.message || "That did not go through.");
    }
    setBusy(false);
  };

  const withdraw = async () => {
    setBusy(true); setError("");
    try {
      await apiFetch(`/events/${eventId}/rsvp`, { method: "DELETE" });
      setMessage("You are off the list.");
      await load();
    } catch (e) {
      setError(e?.message || "That did not go through.");
    }
    setBusy(false);
  };

  const startTicket = async () => {
    setBusy(true); setError("");
    try {
      const r = await apiFetch(`/events/${eventId}/ticket`, { method: "POST" });
      setClientSecret(r.client_secret);
    } catch (e) {
      setError(e?.message || "Could not open the payment.");
    }
    setBusy(false);
  };

  if (error && !data) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-14 text-center" data-testid="event-detail-error">
        <AlertCircle className="mx-auto mb-3 text-rose-600" size={26} />
        <p className="text-gray-700">{error}</p>
      </div>
    );
  }
  if (!data) {
    return <div className="px-5 py-14 text-center text-gray-500"
      data-testid="event-detail-loading">Loading…</div>;
  }

  const ev = data.event;
  const ticket = data.ticket;
  const amountLabel = ticket
    ? `$${((ticket.total_cents || 0) / 100).toFixed(2)}`
    : "";

  return (
    <div className="mx-auto max-w-2xl px-5 py-10" data-testid="event-detail">
      <button onClick={() => navigate(-1)} data-testid="event-back"
        className="mb-5 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
        <ArrowLeft size={15} /> Back
      </button>

      <div className="rounded-2xl border border-[#e4ddd4] bg-white p-6 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-wider text-[#1a5a44]">
          {ev.host_name}
        </p>
        <h1 className="mt-1 font-serif text-3xl text-gray-900" data-testid="event-detail-title">
          {ev.title}
        </h1>

        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-600">
          <span className="flex items-center gap-1.5">
            <Calendar size={15} className="text-gray-400" /> {ev.event_date}
          </span>
          <span className="flex items-center gap-1.5">
            <Clock size={15} className="text-gray-400" /> {ev.event_time} ({ev.timezone})
          </span>
          <span className="flex items-center gap-1.5">
            {ev.venue_mode === "in_person"
              ? <><MapPin size={15} className="text-gray-400" /> {ev.venue_address}</>
              : <><Link2 size={15} className="text-gray-400" /> Online</>}
          </span>
          {ev.kind !== "notice" && (
            <span className="flex items-center gap-1.5" data-testid="event-detail-count">
              <Users size={15} className="text-gray-400" />
              {data.going_count} coming
              {data.spots_left !== null ? ` · ${data.spots_left} places left` : ""}
            </span>
          )}
        </div>

        {ev.description && (
          <p className="mt-5 whitespace-pre-line text-[15px] leading-relaxed text-gray-700">
            {ev.description}
          </p>
        )}

        {message && (
          <p className="mt-5 rounded-lg bg-[#eaf5f0] px-4 py-2.5 text-sm text-[#1a5a44]"
            data-testid="event-detail-message">{message}</p>
        )}
        {error && (
          <p className="mt-5 rounded-lg bg-rose-50 px-4 py-2.5 text-sm text-rose-700"
            data-testid="event-detail-inline-error">{error}</p>
        )}

        <div className="mt-6 border-t border-[#efeae2] pt-5">
          {ev.kind === "notice" && ev.link && (
            <a href={ev.link} target="_blank" rel="noreferrer" data-testid="event-detail-link"
              className="inline-flex items-center gap-2 rounded-lg bg-[#1a5a44] px-4 py-2.5
                         text-sm font-semibold text-white transition hover:bg-[#24755a]">
              <Link2 size={15} /> Open the event page
            </a>
          )}

          {ev.kind === "rsvp" && (
            data.my_state === "going" ? (
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a5a44]"
                  data-testid="event-detail-state">
                  <Check size={16} /> You're going
                </span>
                <button onClick={withdraw} disabled={busy} data-testid="event-withdraw"
                  className="text-sm text-gray-500 underline hover:text-gray-800 disabled:opacity-50">
                  Can't make it
                </button>
              </div>
            ) : data.my_state === "waitlist" ? (
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-amber-700" data-testid="event-detail-state">
                  You're on the waiting list — we'll write if a place frees up.
                </span>
                <button onClick={withdraw} disabled={busy} data-testid="event-withdraw"
                  className="text-sm text-gray-500 underline hover:text-gray-800 disabled:opacity-50">
                  Take me off
                </button>
              </div>
            ) : (
              <button onClick={rsvp} disabled={busy} data-testid="event-rsvp"
                className="rounded-lg bg-[#1a5a44] px-4 py-2.5 text-sm font-semibold text-white
                           transition hover:bg-[#24755a] disabled:opacity-50">
                {data.sold_out ? "Join the waiting list" : "Yes, I'm coming"}
              </button>
            )
          )}

          {ev.kind === "ticketed" && (
            data.my_state === "going" ? (
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1a5a44]"
                data-testid="event-detail-state">
                <Check size={16} /> Your ticket is confirmed
              </span>
            ) : clientSecret ? (
              <Elements stripe={stripePromise}
                options={{ clientSecret, appearance: { theme: "stripe" } }}>
                <TicketForm eventId={eventId} amountLabel={amountLabel}
                  onDone={() => { setClientSecret(null); setMessage("Your ticket is confirmed."); load(); }} />
              </Elements>
            ) : (
              <div>
                <p className="text-sm text-gray-600" data-testid="event-detail-price">
                  <Ticket size={15} className="mr-1.5 inline text-gray-400" />
                  ${Number(ev.price || 0).toFixed(2)} plus {ticket?.fee_label || "card fee"} —
                  {" "}{amountLabel} in total. {ev.refund_policy_text}.
                </p>
                <button onClick={startTicket} disabled={busy || data.sold_out}
                  data-testid="event-buy-ticket"
                  className="mt-3 rounded-lg bg-[#1a5a44] px-4 py-2.5 text-sm font-semibold
                             text-white transition hover:bg-[#24755a] disabled:opacity-50">
                  {data.sold_out ? "Sold out" : "Buy a ticket"}
                </button>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
